import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AnimatedBackground } from '../../components/AnimatedBackground';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';

const PrivacyPolicyPage: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const isSpanish = i18n.language === 'es';

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.nav
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="fixed top-0 w-full z-[9999] bg-white/95 backdrop-blur-md border-b border-blue-100 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-blue-700 hover:text-blue-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{isSpanish ? 'Volver' : 'Back'}</span>
            </button>
            <LanguageSelector variant="compact" className="text-blue-700" />
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <AnimatedBackground variant="hero" className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {isSpanish ? 'Política de Privacidad' : 'Privacy Policy'}
            </h1>
            <p className="text-lg text-blue-200">
              {isSpanish
                ? 'Última actualización: Octubre 2025'
                : 'Last updated: October 2025'}
            </p>
          </motion.div>
        </div>
      </AnimatedBackground>

      {/* Content */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-blue max-w-none"
          >
            {isSpanish ? (
              <>
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introducción</h2>
                  <p className="text-gray-700 leading-relaxed">
                    En ScoutMind AI, nos comprometemos a proteger tu privacidad y los datos personales que compartes con nosotros. Esta política de privacidad describe cómo recopilamos, usamos, almacenamos y protegemos tu información personal cuando utilizas nuestros servicios de consultoría en tecnología deportiva.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Información que Recopilamos</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Recopilamos la siguiente información cuando interactúas con nuestros servicios:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Información de contacto:</strong> Nombre, dirección de correo electrónico, número de teléfono.</li>
                    <li><strong>Información profesional:</strong> Nombre del club, cargo, información relacionada con tus necesidades de consultoría.</li>
                    <li><strong>Datos de navegación:</strong> Dirección IP, tipo de navegador, páginas visitadas, tiempo de permanencia.</li>
                    <li><strong>Cookies:</strong> Información almacenada a través de cookies para mejorar tu experiencia.</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Cómo Usamos tu Información</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Utilizamos tu información personal para:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Responder a tus consultas y solicitudes de servicios.</li>
                    <li>Proporcionar propuestas personalizadas de consultoría tecnológica.</li>
                    <li>Mejorar nuestros servicios y la experiencia del usuario.</li>
                    <li>Enviar comunicaciones relacionadas con nuestros servicios (con tu consentimiento).</li>
                    <li>Cumplir con obligaciones legales y regulatorias.</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Compartir Información</h2>
                  <p className="text-gray-700 leading-relaxed">
                    No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto en los siguientes casos:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>Cuando sea necesario para proporcionar los servicios solicitados.</li>
                    <li>Con proveedores de servicios que nos ayudan a operar nuestro negocio (bajo acuerdos de confidencialidad).</li>
                    <li>Cuando la ley lo requiera o para proteger nuestros derechos legales.</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Seguridad de los Datos</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Implementamos medidas técnicas y organizativas apropiadas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción. Esto incluye el uso de cifrado SSL, servidores seguros y acceso restringido a datos sensibles.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Tus Derechos</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    De acuerdo con el RGPD y la legislación española, tienes derecho a:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Acceso:</strong> Solicitar una copia de tus datos personales.</li>
                    <li><strong>Rectificación:</strong> Corregir información inexacta o incompleta.</li>
                    <li><strong>Supresión:</strong> Solicitar la eliminación de tus datos personales.</li>
                    <li><strong>Oposición:</strong> Oponerte al procesamiento de tus datos.</li>
                    <li><strong>Portabilidad:</strong> Recibir tus datos en un formato estructurado.</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Para ejercer estos derechos, contáctanos en: <a href="mailto:info@scoutmindai.com" className="text-blue-600 hover:text-blue-800">info@scoutmindai.com</a>
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Retención de Datos</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Conservamos tu información personal solo durante el tiempo necesario para cumplir con los propósitos descritos en esta política, o según lo requiera la ley. Cuando ya no sea necesario, eliminaremos o anonimizaremos tus datos de manera segura.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Utilizamos cookies para mejorar tu experiencia en nuestro sitio web. Puedes administrar tus preferencias de cookies a través de la configuración de tu navegador. Para más información, consulta nuestra{' '}
                    <a href="/cookie-policy" className="text-blue-600 hover:text-blue-800">Política de Cookies</a>.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cambios a esta Política</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Podemos actualizar esta política de privacidad periódicamente. Te notificaremos sobre cambios significativos publicando la nueva política en esta página con una fecha de actualización revisada.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contacto</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Si tienes preguntas sobre esta política de privacidad, contáctanos:
                  </p>
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-gray-800"><strong>Email:</strong> <a href="mailto:info@scoutmindai.com" className="text-blue-600 hover:text-blue-800">info@scoutmindai.com</a></p>
                    <p className="text-gray-800"><strong>Teléfono:</strong> +34 608 755 358</p>
                    <p className="text-gray-800"><strong>Ubicación:</strong> España</p>
                  </div>
                </section>
              </>
            ) : (
              <>
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                  <p className="text-gray-700 leading-relaxed">
                    At ScoutMind AI, we are committed to protecting your privacy and the personal data you share with us. This privacy policy describes how we collect, use, store, and protect your personal information when you use our sports technology consulting services.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    We collect the following information when you interact with our services:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Contact information:</strong> Name, email address, phone number.</li>
                    <li><strong>Professional information:</strong> Club name, position, information related to your consulting needs.</li>
                    <li><strong>Navigation data:</strong> IP address, browser type, pages visited, time spent.</li>
                    <li><strong>Cookies:</strong> Information stored through cookies to improve your experience.</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    We use your personal information to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Respond to your inquiries and service requests.</li>
                    <li>Provide personalized technology consulting proposals.</li>
                    <li>Improve our services and user experience.</li>
                    <li>Send communications related to our services (with your consent).</li>
                    <li>Comply with legal and regulatory obligations.</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Sharing Information</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We do not sell, rent, or share your personal information with third parties, except in the following cases:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li>When necessary to provide requested services.</li>
                    <li>With service providers who help us operate our business (under confidentiality agreements).</li>
                    <li>When required by law or to protect our legal rights.</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes the use of SSL encryption, secure servers, and restricted access to sensitive data.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    In accordance with GDPR and Spanish legislation, you have the right to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Access:</strong> Request a copy of your personal data.</li>
                    <li><strong>Rectification:</strong> Correct inaccurate or incomplete information.</li>
                    <li><strong>Deletion:</strong> Request the deletion of your personal data.</li>
                    <li><strong>Opposition:</strong> Object to the processing of your data.</li>
                    <li><strong>Portability:</strong> Receive your data in a structured format.</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    To exercise these rights, contact us at: <a href="mailto:info@scoutmindai.com" className="text-blue-600 hover:text-blue-800">info@scoutmindai.com</a>
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We retain your personal information only for as long as necessary to fulfill the purposes described in this policy, or as required by law. When no longer needed, we will securely delete or anonymize your data.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We use cookies to improve your experience on our website. You can manage your cookie preferences through your browser settings. For more information, see our{' '}
                    <a href="/cookie-policy" className="text-blue-600 hover:text-blue-800">Cookie Policy</a>.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to this Policy</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We may update this privacy policy periodically. We will notify you of significant changes by posting the new policy on this page with a revised update date.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact</h2>
                  <p className="text-gray-700 leading-relaxed">
                    If you have questions about this privacy policy, contact us:
                  </p>
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-gray-800"><strong>Email:</strong> <a href="mailto:info@scoutmindai.com" className="text-blue-600 hover:text-blue-800">info@scoutmindai.com</a></p>
                    <p className="text-gray-800"><strong>Phone:</strong> +34 608 755 358</p>
                    <p className="text-gray-800"><strong>Location:</strong> Spain</p>
                  </div>
                </section>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
